import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { EMAIL_EDITOR_ENABLED } from '../config/features';
import {
  Rocket, Bell, Settings, LogOut,
  Menu, X, RefreshCw, MessageCircle,
  CalendarDays, BookCheck, Tag, MessageSquare, Building2, Gauge, Database,
  MessageSquarePlus, Globe, Banknote, BarChart3, Star, Smartphone, CalendarRange, FileText, MailCheck, ShieldCheck, Users, Newspaper, UserRound,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import LanguageSelector from './LanguageSelector';
import { OnboardingNudge } from './OnboardingNudge';
import EmailVerificationBanner from './EmailVerificationBanner';

interface Props {
  children: ReactNode;
}

export default function ClientPortalLayout({ children }: Props) {
  const { user, logout } = useClientAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const onboardingItems = [
    { to: '/portal/dashboard', icon: Rocket, label: t('portal.nav.onboarding', 'Onboarding') },
  ];

  // Post-onboarding owner management (the product).
  const manageItems = [
    { to: '/portal/manage/dashboard', icon: Gauge, label: t('portal.manage.nav.dashboard') },
    { to: '/portal/manage/analytics', icon: BarChart3, label: t('portal.manage.nav.analytics', 'Estadísticas') },
    { to: '/portal/manage/properties', icon: Building2, label: t('portal.manage.nav.properties') },
    { to: '/portal/manage/bookings', icon: BookCheck, label: t('portal.manage.nav.bookings') },
    { to: '/portal/manage/guests', icon: Users, label: t('portal.manage.nav.guests', 'Huéspedes') },
    { to: '/portal/manage/calendar', icon: CalendarDays, label: t('portal.manage.nav.calendar') },
    { to: '/portal/manage/timeline', icon: CalendarRange, label: t('portal.manage.nav.timeline', 'Multi-calendario') },
    { to: '/portal/manage/pricing', icon: Tag, label: t('portal.manage.nav.pricing') },
    { to: '/portal/manage/payouts', icon: Banknote, label: t('portal.manage.nav.payouts', 'Cobros') },
    { to: '/portal/manage/owners', icon: UserRound, label: t('portal.manage.nav.owners', 'Propietarios') },
    { to: '/portal/manage/reports', icon: FileText, label: t('portal.manage.nav.reports', 'Informes') },
    { to: '/portal/manage/messages', icon: MessageSquare, label: t('portal.manage.nav.messages') },
    { to: '/portal/manage/auto-messages', icon: MailCheck, label: t('portal.manage.nav.autoMessages', 'Mensajes automáticos') },
    // Newsletter is part of the in-development email-editor surface — dev only.
    ...(EMAIL_EDITOR_ENABLED ? [{ to: '/portal/manage/newsletter', icon: Newspaper, label: t('portal.manage.nav.newsletter', 'Newsletter') }] : []),
    { to: '/portal/manage/reviews', icon: Star, label: t('portal.manage.nav.reviews', 'Reseñas') },
    { to: '/portal/manage/guest-app', icon: Smartphone, label: t('portal.manage.nav.guestApp', 'App del huésped') },
    { to: '/portal/manage/ses', icon: ShieldCheck, label: t('portal.manage.nav.ses', 'Registro viajeros (SES)') },
    { to: '/portal/my-site', icon: Globe, label: t('portal.nav.mySite', 'Mi web') },
  ];

  const accountItems = [
    { to: '/portal/settings', icon: Settings, label: t('portal.nav.settings') },
    { to: '/portal/manage/settings', icon: Database, label: t('portal.nav.data', 'Mis datos') },
    { to: '/portal/feedback', icon: MessageSquarePlus, label: t('portal.nav.feedback', 'Feedback') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const openChat = () => {
    setSidebarOpen(false);
    window.$crisp?.push(['do', 'chat:open']);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 text-white
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        flex flex-col
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700">
          <a href="https://kanarix.es" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3" aria-label="Kanarix.es">
            <img src="/kanarix-logo-white.png" alt="Kanarix" className="h-14 w-auto" />
          </a>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Client info */}
        {user && (
          <div className="px-6 py-4 border-b border-slate-700">
            <p className="text-sm font-medium text-white truncate">{user.name}</p>
            <p className="text-xs text-slate-400 truncate">{user.client_name}</p>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('portal.nav.groupOnboarding', 'Alta')}</p>
          {onboardingItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive ? 'bg-teal-600/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
              `}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('portal.nav.groupManage', 'Gestión')}</p>
          {manageItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive ? 'bg-teal-600/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
              `}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('portal.nav.groupAccount', 'Cuenta')}</p>
          {accountItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive ? 'bg-teal-600/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
              `}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          {/* Notifications with badge */}
          <NavLink
            to="/portal/notifications"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `
              flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${isActive
                ? 'bg-teal-600/20 text-teal-400'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
            `}
          >
            <div className="relative">
              <Bell size={18} />
              {user && user.unread_notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-[10px] font-bold flex items-center justify-center">
                  {user.unread_notifications > 9 ? '9+' : user.unread_notifications}
                </span>
              )}
            </div>
            {t('portal.nav.notifications')}
          </NavLink>
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-slate-700 space-y-1">
          <button
            onClick={openChat}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-teal-300 hover:bg-slate-800 hover:text-teal-200 transition-colors"
          >
            <MessageCircle size={18} />
            {t('portal.nav.help')}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            {t('portal.nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-slate-200 px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
            >
              <Menu size={22} />
            </button>

            <div className="flex items-center gap-3 ml-auto">              <LanguageSelector />              {user && user.unread_notifications > 0 && (
                <NavLink to="/portal/notifications" className="relative p-2 text-slate-500 hover:text-slate-700">
                  <Bell size={20} />
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-rose-500" />
                </NavLink>
              )}
              <button
                onClick={() => window.location.reload()}
                className="p-2 text-slate-400 hover:text-slate-600"
                title={t('portal.nav.refresh')}
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <EmailVerificationBanner />
          {children}
        </main>
      </div>

      {/* Floating onboarding nudge — visible until the alta is finished */}
      <OnboardingNudge />
    </div>
  );
}
